/**
 * NearbyVehicleServices
 *
 * Shows a curated list of local garages, tyre fitters, car detailers, MOT stations
 * and breakdown recovery - with type filter, DEMO badge when the parent listing
 * is a demo, and a click-to-call phone action.
 *
 * For the MVP we render demo data client-side. Later this can be swapped for a
 * `GET /api/vehicles/nearby-services?lat=&lng=&type=` endpoint that reads real
 * partner records.
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Wrench, ShieldCheck, Sparkles, Truck, GaugeCircle, Phone, MapPin, Star } from 'lucide-react';

const SERVICE_TYPES_UK = [
  { key: 'garage', label: 'Garages', icon: Wrench, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { key: 'mot', label: 'MOT Stations', icon: ShieldCheck, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'tyres', label: 'Tyre Fitting', icon: GaugeCircle, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { key: 'detailing', label: 'Valet & Detailing', icon: Sparkles, color: 'text-pink-600 bg-pink-50 border-pink-200' },
  { key: 'breakdown', label: 'Recovery & Breakdown', icon: Truck, color: 'text-red-600 bg-red-50 border-red-200' },
];

// SL uses VET (Vehicle Emission Test) instead of MOT
const SERVICE_TYPES_SL = [
  { key: 'garage', label: 'Garages', icon: Wrench, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { key: 'mot', label: 'VET Centres', icon: ShieldCheck, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'tyres', label: 'Tyre Fitting', icon: GaugeCircle, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { key: 'detailing', label: 'Valet & Detailing', icon: Sparkles, color: 'text-pink-600 bg-pink-50 border-pink-200' },
  { key: 'breakdown', label: 'Recovery & Breakdown', icon: Truck, color: 'text-red-600 bg-red-50 border-red-200' },
];

// 2 demo services per type - kept UK-flavoured (matches your demo listings)
const DEMO_SERVICES_UK = [
  // Garages
  { id: 'g1', type: 'garage', name: 'Halfords Autocentre - Manchester Central',
    rating: 4.7, reviews: 1284, distance_mi: 1.2, phone: '0161 200 1188',
    address: 'Deansgate, Manchester M3 4LZ',
    tags: ['MOT', 'Diagnostics', 'Brake Service'], price_from: 79 },
  { id: 'g2', type: 'garage', name: 'Kwik Fit - Salford Quays',
    rating: 4.5, reviews: 892, distance_mi: 2.4, phone: '0161 872 4455',
    address: 'The Quays, Salford M50 3AH',
    tags: ['Servicing', 'Exhausts', 'Clutches'], price_from: 89 },
  // MOT
  { id: 'm1', type: 'mot', name: 'Speedy MOT Centre - Oldham',
    rating: 4.9, reviews: 421, distance_mi: 3.1, phone: '0161 626 7788',
    address: 'Rochdale Rd, Oldham OL1 2ER',
    tags: ['Class 4 MOT', 'While-U-Wait'], price_from: 45 },
  { id: 'm2', type: 'mot', name: 'AutoStop MOT - Trafford Park',
    rating: 4.6, reviews: 337, distance_mi: 1.9, phone: '0161 872 9911',
    address: 'Trafford Park M17 1DR',
    tags: ['Class 4 & 7 MOT', 'Free Retest'], price_from: 40 },
  // Tyres
  { id: 't1', type: 'tyres', name: 'ATS Euromaster - Manchester',
    rating: 4.4, reviews: 611, distance_mi: 0.8, phone: '0161 236 4523',
    address: 'Bury New Rd, M8 8FL',
    tags: ['Michelin', 'Continental', 'Wheel Alignment'], price_from: 55 },
  { id: 't2', type: 'tyres', name: 'National Tyres - Stockport',
    rating: 4.6, reviews: 488, distance_mi: 5.2, phone: '0161 480 2299',
    address: 'Wellington Rd, Stockport SK1 3HZ',
    tags: ['Same-Day Fitting', 'Puncture Repair'], price_from: 49 },
  // Detailing
  { id: 'd1', type: 'detailing', name: 'Gleam & Go Mobile Detailing',
    rating: 4.9, reviews: 156, distance_mi: 0, phone: '07890 456 123',
    address: 'Mobile - comes to you',
    tags: ['Full Valet', 'Ceramic Coat', 'Mobile'], price_from: 65 },
  { id: 'd2', type: 'detailing', name: 'Shine On Detailing Studio',
    rating: 4.8, reviews: 224, distance_mi: 3.5, phone: '0161 224 9977',
    address: 'Chorlton Rd, M15 4AN',
    tags: ['Paint Correction', 'Interior Deep Clean'], price_from: 120 },
  // Breakdown
  { id: 'b1', type: 'breakdown', name: 'AA Breakdown - Priority Response',
    rating: 4.5, reviews: 3120, distance_mi: null, phone: '0800 88 77 66',
    address: 'Nationwide',
    tags: ['24/7', 'Roadside', 'Home Start'], price_from: 6 },
  { id: 'b2', type: 'breakdown', name: 'Green Flag Recovery',
    rating: 4.6, reviews: 2148, distance_mi: null, phone: '0800 400 600',
    address: 'Nationwide',
    tags: ['24/7', 'European Cover Add-on'], price_from: 5 },
];

// Sri Lanka variants - trusted Colombo/Kandy/Galle garages, MOT (VET), tyres and recovery
const DEMO_SERVICES_SL = [
  // Garages
  { id: 'g1-sl', type: 'garage', name: 'AutoLanka Service Centre - Rajagiriya',
    rating: 4.6, reviews: 842, distance_mi: 1.5, phone: '011 288 4455',
    address: 'Sri Jayawardenepura Kotte Rd, Rajagiriya',
    tags: ['Full Service', 'AC Repair', 'Diagnostics'], price_from: 6500 },
  { id: 'g2-sl', type: 'garage', name: 'Ideal Motors - Colombo 5',
    rating: 4.7, reviews: 1120, distance_mi: 3.2, phone: '011 250 1121',
    address: 'Havelock Rd, Colombo 05',
    tags: ['Toyota', 'Nissan', 'Original Parts'], price_from: 7500 },
  // MOT (VET - Vehicle Emission Test in Sri Lanka)
  { id: 'm1-sl', type: 'mot', name: 'Cleanco VET Centre - Kaduwela',
    rating: 4.5, reviews: 268, distance_mi: 4.2, phone: '011 253 4455',
    address: 'Athurugiriya Rd, Kaduwela',
    tags: ['VET Certificate', 'Same-Day'], price_from: 1500 },
  { id: 'm2-sl', type: 'mot', name: 'Laugfs Eco VET - Nugegoda',
    rating: 4.4, reviews: 312, distance_mi: 2.6, phone: '011 285 7788',
    address: 'High Level Rd, Nugegoda',
    tags: ['Petrol + Diesel VET', 'Free Retest'], price_from: 1200 },
  // Tyres
  { id: 't1-sl', type: 'tyres', name: 'DSI Tyres - Colombo 03',
    rating: 4.5, reviews: 522, distance_mi: 1.1, phone: '011 257 9911',
    address: 'Union Place, Colombo 03',
    tags: ['DSI', 'MRF', 'Alignment'], price_from: 12500 },
  { id: 't2-sl', type: 'tyres', name: 'CEAT Tyre Centre - Kandy',
    rating: 4.7, reviews: 388, distance_mi: 5.8, phone: '081 220 1145',
    address: 'Katugastota Rd, Kandy',
    tags: ['CEAT', 'Yokohama', 'Puncture Repair'], price_from: 10500 },
  // Detailing
  { id: 'd1-sl', type: 'detailing', name: 'Autoshine Mobile Detailers',
    rating: 4.8, reviews: 194, distance_mi: 0, phone: '077 555 4433',
    address: 'Mobile - Colombo & suburbs',
    tags: ['Full Valet', 'Ceramic Coat', 'Mobile'], price_from: 6500 },
  { id: 'd2-sl', type: 'detailing', name: 'Prime Auto Spa - Battaramulla',
    rating: 4.6, reviews: 268, distance_mi: 4.4, phone: '011 288 3344',
    address: 'Pannipitiya Rd, Battaramulla',
    tags: ['Interior Deep Clean', 'Engine Bay'], price_from: 8500 },
  // Breakdown
  { id: 'b1-sl', type: 'breakdown', name: 'AA Ceylon Roadside Rescue',
    rating: 4.4, reviews: 1420, distance_mi: null, phone: '011 242 1528',
    address: 'Islandwide',
    tags: ['24/7', 'Roadside Repair', 'Recovery'], price_from: 3500 },
  { id: 'b2-sl', type: 'breakdown', name: 'On-Time Vehicle Recovery',
    rating: 4.6, reviews: 812, distance_mi: null, phone: '077 122 3344',
    address: 'Islandwide',
    tags: ['24/7', 'Flatbed', 'Breakdown Tow'], price_from: 4500 },
];

const NearbyVehicleServices = ({ isDemo = false, listing }) => {
  const [activeType, setActiveType] = useState('garage');
  const country = (listing?.country || '').toUpperCase();
  const isSL = country === 'LK' || country === 'SL';
  const services = isSL ? DEMO_SERVICES_SL : DEMO_SERVICES_UK;
  const serviceTypes = isSL ? SERVICE_TYPES_SL : SERVICE_TYPES_UK;
  const currencyPrefix = isSL ? 'Rs. ' : '£';
  const distanceUnit = isSL ? 'km' : 'mi';

  const filtered = useMemo(
    () => services.filter((s) => s.type === activeType),
    [activeType, services]
  );

  const activeMeta = serviceTypes.find((t) => t.key === activeType);
  const ActiveIcon = activeMeta?.icon || Wrench;

  return (
    <Card id="services" className="scroll-mt-24" data-testid="nearby-vehicle-services">
      <CardHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="h-5 w-5 text-emerald-600" />
          <CardTitle className="text-lg">Nearby Services</CardTitle>
          <Badge variant="outline" className="text-xs">Live directory</Badge>
          {isDemo && (
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="nearby-services-demo-badge">
              DEMO
            </Badge>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Trusted garages, MOT centres, tyre fitters and recovery services near {listing?.location || 'you'}.
        </p>
      </CardHeader>
      <CardContent>
        {/* Type filter pills */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="nearby-services-tabs">
          {serviceTypes.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeType;
            return (
              <button
                key={t.key}
                onClick={() => setActiveType(t.key)}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all
                  ${isActive
                    ? t.color + ' shadow-sm'
                    : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50'}
                `}
                data-testid={`nearby-services-tab-${t.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Services list */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s) => (
            <div
              key={s.id}
              className="relative border border-gray-200 rounded-lg p-3 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
              data-testid={`nearby-service-${s.id}`}
            >
              {isDemo && (
                <div className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl" data-testid={`nearby-service-demo-${s.id}`}>
                  DEMO
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${activeMeta?.color || 'bg-gray-50'}`}>
                  <ActiveIcon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">{s.name}</h4>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span className="flex items-center gap-0.5 text-amber-600">
                      <Star className="h-3 w-3 fill-current" />
                      {s.rating.toFixed(1)}
                    </span>
                    <span>({s.reviews.toLocaleString()} reviews)</span>
                    {s.distance_mi !== null && (
                      <span>· {s.distance_mi.toFixed(1)} {distanceUnit}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">{s.address}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {s.tags.map((tag) => (
                      <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs font-semibold text-emerald-700">
                      From {currencyPrefix}{s.price_from.toLocaleString()}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => window.open(`tel:${s.phone.replace(/\s/g, '')}`, '_self')}
                      data-testid={`nearby-service-call-${s.id}`}
                    >
                      <Phone className="h-3 w-3 mr-1" />
                      Call
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-4 italic">
          Services shown are curated partners - availability and pricing may vary. Contact each provider directly for confirmed quotes.
        </p>
      </CardContent>
    </Card>
  );
};

export default NearbyVehicleServices;
